import { z } from 'zod';
import {
  ADJUSTMENT_REASONS,
  BASE_UNITS,
  COSTING_METHODS,
  DAY_CLOSE_MODES,
  DEDUCT_ON,
  DENOMINATIONS,
  DEFAULT_OPERATIONS_SETTINGS,
  WASTAGE_REASONS,
} from '../constants/operations.js';
import { email, gstin, isoDate, name, objectId, optional, pagination, phone, requiredText, text } from './common.js';

const paise = z.coerce.number().int().min(0).max(100_000_000_00);
const qty = z.coerce.number().positive().max(100_000_000);
const unit = z.string().trim().min(1).max(40);
const rateBp = z.coerce.number().int().min(0).max(10_000);

// ---------------------------------------------------------------- settings (Settings → Shifts & inventory)
const keys = (list) => list.map((x) => x.key);
export const shiftSettingsSchema = z.strictObject({
  blindClose: z.boolean(),
  requireOpenShift: z.boolean(),
  dayClose: z.enum(keys(DAY_CLOSE_MODES)),
  emailZReport: z.boolean(),
  cashOutCategories: z.array(requiredText(40)).min(1, 'Keep at least one category').max(20),
  // No longer used (one shift per outlet): accepted so settings saved before still save.
  mode: z.string().max(20).optional(),
  oneOutletAtATime: z.boolean().optional(),
});
export const inventorySettingsSchema = z.strictObject({
  allowNegativeStock: z.boolean(),
  costing: z.enum(keys(COSTING_METHODS)),
  deductOn: z.enum(keys(DEDUCT_ON)),
  restockOnRefund: z.boolean(),
  separatePurchaseGst: z.boolean(),
  lowStockEmail: z.boolean(),
  expiryWarningDays: z.coerce.number().int().min(0).max(60),
});
export const operationsSettingsFields = {
  shifts: shiftSettingsSchema.default({
    ...DEFAULT_OPERATIONS_SETTINGS.shifts,
    cashOutCategories: [...DEFAULT_OPERATIONS_SETTINGS.shifts.cashOutCategories],
  }),
  inventory: inventorySettingsSchema.default({ ...DEFAULT_OPERATIONS_SETTINGS.inventory }),
};

// ---------------------------------------------------------------- shifts & cash
/** Notes/coins counted: { "500": 3, "100": 7, … } → only known denominations. */
const denominationCounts = z
  .record(z.string(), z.coerce.number().int().min(0).max(100_000))
  .refine((r) => Object.keys(r).every((k) => DENOMINATIONS.includes(Number(k))), 'Unknown denomination')
  .default({});

export const openShiftSchema = z.strictObject({
  openingFloatPaise: paise,
  denominations: denominationCounts.optional(),
  note: optional(text(200)),
});

export const cashEntrySchema = z
  .strictObject({
    type: z.enum(['in', 'out']),
    amountPaise: paise.refine((v) => v > 0, 'Enter an amount'),
    category: optional(text(40)),
    note: optional(text(160)),
  })
  .refine((e) => e.type === 'in' || Boolean(e.category), { message: 'Choose a category', path: ['category'] });

export const closeShiftSchema = z.strictObject({
  denominations: denominationCounts,
  otherPaise: paise.default(0),
  note: optional(text(300)),
});

export const dayCloseSchema = z.strictObject({
  businessDate: isoDate,
  note: optional(text(300)),
});

export const shiftListQuery = z.strictObject({
  from: optional(isoDate),
  to: optional(isoDate),
  status: z.enum(['open', 'closed']).optional(),
  ...pagination,
});

export const emailReportSchema = z.strictObject({ to: optional(email) });

// ---------------------------------------------------------------- inventory
export const stockItemSchema = z.strictObject({
  name: requiredText(80),
  category: optional(text(40)),
  baseUnit: z.enum(BASE_UNITS),
  packs: z
    .array(z.strictObject({ name: requiredText(20), factor: qty }))
    .max(5)
    .default([]),
  reorderLevel: z.coerce.number().min(0).max(100_000_000).default(0),
  trackBatches: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

const ingredient = z.strictObject({ stockItemId: objectId, qty });
export const recipeSchema = z.strictObject({
  target: z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('item'), itemId: objectId, variantId: optional(objectId) }),
    z.strictObject({ kind: z.literal('option'), groupId: objectId, optionId: objectId }),
  ]),
  ingredients: z.array(ingredient).max(40),
});

export const supplierSchema = z.strictObject({
  name: requiredText(120),
  contactName: optional(name),
  phone: optional(phone),
  email: optional(email),
  gstin: optional(gstin),
  address: optional(text(300)),
  notes: optional(text(500)),
  isActive: z.boolean().default(true),
});

const purchaseLine = z.strictObject({
  stockItemId: objectId,
  unit,
  qty,
  ratePaise: paise,
  gstRateBp: rateBp.default(0),
});

export const purchaseOrderSchema = z.strictObject({
  outletId: objectId,
  supplierId: objectId,
  expectedDate: optional(isoDate),
  notes: optional(text(500)),
  lines: z.array(purchaseLine).min(1, 'Add at least one item').max(200),
});

export const goodsReceiptSchema = z.strictObject({
  outletId: objectId,
  supplierId: objectId,
  purchaseOrderId: optional(objectId),
  supplierInvoiceNo: optional(text(40)),
  invoiceDate: optional(isoDate),
  notes: optional(text(500)),
  lines: z
    .array(purchaseLine.extend({ batchNo: optional(text(40)), expiryDate: optional(isoDate) }))
    .min(1, 'Add at least one item')
    .max(200),
});

export const adjustmentSchema = z.strictObject({
  outletId: objectId,
  reason: z.enum(ADJUSTMENT_REASONS),
  note: optional(text(300)),
  lines: z
    .array(
      z.strictObject({
        stockItemId: objectId,
        unit,
        qty: z.coerce
          .number()
          .refine((v) => v !== 0, 'Enter a quantity')
          .refine((v) => Math.abs(v) <= 100_000_000, 'Too large'),
        unitCostPaise: z.coerce.number().min(0).max(100_000_000).optional(),
      }),
    )
    .min(1)
    .max(200),
});

export const wastageSchema = z.strictObject({
  outletId: objectId,
  reason: z.enum(WASTAGE_REASONS),
  note: optional(text(300)),
  lines: z
    .array(z.strictObject({ stockItemId: objectId, unit, qty, batchId: optional(objectId) }))
    .min(1)
    .max(200),
});

export const transferSchema = z
  .strictObject({
    fromOutletId: objectId,
    toOutletId: objectId,
    note: optional(text(300)),
    lines: z
      .array(z.strictObject({ stockItemId: objectId, unit, qty }))
      .min(1)
      .max(200),
  })
  .refine((t) => t.fromOutletId !== t.toOutletId, { message: 'Choose a different outlet', path: ['toOutletId'] });

export const stockCountCreateSchema = z.strictObject({
  outletId: objectId,
  category: optional(text(40)),
  note: optional(text(300)),
});

export const stockCountUpdateSchema = z.strictObject({
  lines: z
    .array(z.strictObject({ stockItemId: objectId, countedQty: z.coerce.number().min(0).max(100_000_000).nullable() }))
    .max(2000),
});

export const stockQuery = z.strictObject({
  outletId: optional(objectId),
  q: optional(text(60)),
  category: optional(text(40)),
  low: z.enum(['1', '0']).optional(),
});

export const ledgerQuery = z.strictObject({
  outletId: optional(objectId),
  stockItemId: optional(objectId),
  type: optional(text(30)),
  from: optional(isoDate),
  to: optional(isoDate),
  ...pagination,
});

export const purchaseListQuery = z.strictObject({
  outletId: optional(objectId),
  supplierId: optional(objectId),
  status: optional(text(30)),
  ...pagination,
});
